// Fine-stroke inline icons (Lucide geometry), built via createElementNS so they
// obey the strict style-src/script-src CSP — presentation attributes only, no
// inline <style>. `stroke: currentColor` lets a single icon inherit whatever
// text colour its context sets, which keeps them theme-aware for free.

const SVG_NS = 'http://www.w3.org/2000/svg';

type Shape = { tag: 'path' | 'circle' | 'line' | 'polyline' | 'rect'; [attr: string]: string };

const ICONS: Record<string, Shape[]> = {
  user: [
    { tag: 'path', d: 'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' },
    { tag: 'circle', cx: '12', cy: '7', r: '4' },
  ],
  users: [
    { tag: 'path', d: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' },
    { tag: 'circle', cx: '9', cy: '7', r: '4' },
    { tag: 'path', d: 'M23 21v-2a4 4 0 0 0-3-3.87' },
    { tag: 'path', d: 'M16 3.13a4 4 0 0 1 0 7.75' },
  ],
  share: [
    { tag: 'circle', cx: '18', cy: '5', r: '3' },
    { tag: 'circle', cx: '6', cy: '12', r: '3' },
    { tag: 'circle', cx: '18', cy: '19', r: '3' },
    { tag: 'line', x1: '8.59', y1: '13.51', x2: '15.42', y2: '17.49' },
    { tag: 'line', x1: '15.41', y1: '6.51', x2: '8.59', y2: '10.49' },
  ],
  activity: [{ tag: 'polyline', points: '22 12 18 12 15 21 9 3 6 12 2 12' }],
  plus: [
    { tag: 'line', x1: '12', y1: '5', x2: '12', y2: '19' },
    { tag: 'line', x1: '5', y1: '12', x2: '19', y2: '12' },
  ],
  copy: [
    { tag: 'rect', x: '9', y: '9', width: '13', height: '13', rx: '2', ry: '2' },
    { tag: 'path', d: 'M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' },
  ],
  check: [{ tag: 'polyline', points: '20 6 9 17 4 12' }],
  back: [{ tag: 'polyline', points: '15 18 9 12 15 6' }],
  trash: [
    { tag: 'polyline', points: '3 6 5 6 21 6' },
    { tag: 'path', d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' },
  ],
  shield: [
    { tag: 'path', d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
    { tag: 'polyline', points: '9 12 11 14 15 10' },
  ],
  edit: [
    { tag: 'path', d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' },
    { tag: 'path', d: 'M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z' },
  ],
  refresh: [
    { tag: 'polyline', points: '23 4 23 10 17 10' },
    { tag: 'path', d: 'M20.49 15a9 9 0 1 1-2.12-9.36L23 10' },
  ],
  qr: [
    { tag: 'rect', x: '3', y: '3', width: '7', height: '7', rx: '1' },
    { tag: 'rect', x: '14', y: '3', width: '7', height: '7', rx: '1' },
    { tag: 'rect', x: '3', y: '14', width: '7', height: '7', rx: '1' },
    { tag: 'line', x1: '14', y1: '14', x2: '17', y2: '14' },
    { tag: 'line', x1: '20', y1: '14', x2: '21', y2: '14' },
    { tag: 'line', x1: '17', y1: '17', x2: '17', y2: '21' },
    { tag: 'line', x1: '21', y1: '17', x2: '21', y2: '21' },
  ],
  key: [
    { tag: 'circle', cx: '7.5', cy: '15.5', r: '5.5' },
    { tag: 'path', d: 'M11.5 11.5 22 1' },
    { tag: 'path', d: 'M17 6l3 3' },
    { tag: 'path', d: 'M14 9l3 3' },
  ],
  panel: [
    { tag: 'rect', x: '3', y: '3', width: '18', height: '18', rx: '2' },
    { tag: 'line', x1: '9', y1: '3', x2: '9', y2: '21' },
  ],
  arrow: [
    { tag: 'line', x1: '4', y1: '12', x2: '19', y2: '12' },
    { tag: 'polyline', points: '13 6 19 12 13 18' },
  ],
  lock: [
    { tag: 'rect', x: '4', y: '10', width: '16', height: '11', rx: '2' },
    { tag: 'path', d: 'M8 10V7a4 4 0 0 1 8 0v3' },
  ],
  search: [
    { tag: 'circle', cx: '11', cy: '11', r: '7' },
    { tag: 'line', x1: '21', y1: '21', x2: '16.65', y2: '16.65' },
  ],
  download: [
    { tag: 'path', d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' },
    { tag: 'polyline', points: '7 10 12 15 17 10' },
    { tag: 'line', x1: '12', y1: '15', x2: '12', y2: '3' },
  ],
  coffee: [
    { tag: 'path', d: 'M18 8h1a4 4 0 0 1 0 8h-1' },
    { tag: 'path', d: 'M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z' },
    { tag: 'line', x1: '6', y1: '1', x2: '6', y2: '4' },
    { tag: 'line', x1: '10', y1: '1', x2: '10', y2: '4' },
    { tag: 'line', x1: '14', y1: '1', x2: '14', y2: '4' },
  ],
  close: [
    { tag: 'line', x1: '18', y1: '6', x2: '6', y2: '18' },
    { tag: 'line', x1: '6', y1: '6', x2: '18', y2: '18' },
  ],
  info: [
    { tag: 'circle', cx: '12', cy: '12', r: '10' },
    { tag: 'line', x1: '12', y1: '16', x2: '12', y2: '12' },
    { tag: 'line', x1: '12', y1: '8', x2: '12.01', y2: '8' },
  ],
  settings: [
    { tag: 'circle', cx: '12', cy: '12', r: '3' },
    {
      tag: 'path',
      d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
    },
  ],
};

export type IconName = keyof typeof ICONS;

export function icon(name: IconName, size = 20, filled = false): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  // Open subpaths (e.g. the head/body outlines) are auto-closed by the SVG
  // fill rule with a straight line, which is what turns them solid here —
  // no separate filled artwork needed. Lines (e.g. the "share" connectors)
  // have no fill area, so they stay visible via stroke either way.
  svg.setAttribute('fill', filled ? 'currentColor' : 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add('icon');
  for (const { tag, ...attrs } of ICONS[name] ?? []) {
    const el = document.createElementNS(SVG_NS, tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    svg.append(el);
  }
  return svg;
}
